import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivateFn, Router } from '@angular/router';
import { combineLatest, merge, timer } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { SettingsService } from '../settings/settings.service';
import { UserProfileService } from '../user/user-profile.service';
import { TBlkCxpRole } from '../../types/settingsDataModule';

/**
 * Roles that may open API traffic (/organization/smsc-reports). The sidebar
 * link reads canViewApiTraffic() too, so the two always agree.
 */
export const API_TRAFFIC_ROLES: readonly TBlkCxpRole[] = [
  'SUPER_ADMIN',
  'ROLE_BLKKCXP_ADMINISTRATOR',
  'ROLE_BLKKCXP_ACCOUNT_MANAGER',
  'ROLE_BLKKCXP_INTEGRATIONS_MANAGER',
];

/** Where someone without access lands instead: the delivery reports. */
export const API_TRAFFIC_FALLBACK_URL = '/organization/reports';

/** How long to wait for the profile (and its roles) before deciding without it. */
const PROFILE_WAIT_MS = 8000;

export function canViewApiTraffic(roles: readonly string[] | null | undefined): boolean {
  return (roles ?? []).some((role) => (API_TRAFFIC_ROLES as readonly string[]).includes(role));
}

/**
 * Keeps people without an API traffic role out of the page when they type
 * the URL, not only out of the sidebar link.
 *
 * The roles in the access token decide straight away when they grant access.
 * Otherwise the guard waits for the profile roles the sidebar uses (loading
 * the profile if nobody has yet), and sends anyone without a permitted role
 * to the delivery reports. If the profile cannot be read in time, access is
 * refused rather than assumed.
 */
export const apiTrafficGuard: CanActivateFn = () => {
  // The server has no session; the console's auth guard renders nothing there.
  if (!isPlatformBrowser(inject(PLATFORM_ID))) {
    return false;
  }

  const settings = inject(SettingsService);
  const user = inject(UserProfileService);
  const router = inject(Router);
  const refuse = () => router.createUrlTree([API_TRAFFIC_FALLBACK_URL]);

  const tokenRoles = user.userDetails?.realm_access?.roles;
  if (canViewApiTraffic(Array.isArray(tokenRoles) ? tokenRoles : [])) {
    return true;
  }

  let requested = false;
  const profileRoles = combineLatest([settings.getUserRoles, settings.getUserProfileData]).pipe(
    tap(([roles, profile]) => {
      if (!requested && !profile && !(roles ?? []).length) {
        requested = true;
        settings.fetchUserProfile();
      }
    }),
    filter(([roles, profile]) => (roles ?? []).length > 0 || !!profile),
    // fetchUserProfile publishes the profile before the roles subject, so the
    // first emission after it arrives can be ([], profile). Read the roles off
    // the profile itself so that emission already carries them.
    map(([roles, profile]) => [...(roles ?? []), ...(profile?.realmRoles ?? [])])
  );
  const profileFailed = settings.onErrorMessage.pipe(
    filter((error) => error.source === 'fetchUserProfile'),
    map(() => [] as TBlkCxpRole[])
  );
  const gaveUp = timer(PROFILE_WAIT_MS).pipe(map(() => [] as TBlkCxpRole[]));

  return merge(profileRoles, profileFailed, gaveUp).pipe(
    take(1),
    map((roles) => (canViewApiTraffic(roles) ? true : refuse()))
  );
};
